
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, adminOperation } from '../../utils/supabase';
import type { AdoptionCaseType as AdoptionCase, UserType as User } from '../../utils/supabase';
import ContactFooter from '../../components/feature/ContactFooter';

export default function AdoptionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [adoptionCase, setAdoptionCase] = useState<AdoptionCase | null>(null);
  const [shelterInfo, setShelterInfo] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAdoptionForm, setShowAdoptionForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [submittingForm, setSubmittingForm] = useState(false);

  // Load adoption data on component mount or when id changes
  useEffect(() => {
    if (id) {
      loadAdoptionData();
    }
  }, [id]);

  const loadAdoptionData = async () => {
    try {
      setLoading(true);
      
      // Cargar caso de adopción directamente desde la base de datos
      const { data: adoptionData, error: adoptionError } = await supabase
        .from('adoption_cases')
        .select('*')
        .eq('id', id)
        .single();

      if (adoptionError) {
        console.error('Error cargando caso de adopción:', adoptionError);
        navigate('/');
        return;
      }

      setAdoptionCase(adoptionData);

      // Cargar información del refugio/usuario responsable
      if (adoptionData?.user_id) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', adoptionData.user_id)
          .single();

        if (userError) {
          console.error('Error cargando información del refugio:', userError);
        } else {
          setShelterInfo(userData);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  // Generar galería de imágenes usando las imágenes reales del caso
  const dogImages = adoptionCase ? [
    adoptionCase.image_url,
    adoptionCase.image_url_2,
    adoptionCase.image_url_3,
    adoptionCase.image_url_4
  ].filter(Boolean) : [];

  // Si no hay imágenes, usar imágenes por defecto
  const displayImages = dogImages.length > 0 ? dogImages : [
    'https://readdy.ai/api/search-image?query=Adorable%20rescue%20dog%20portrait%2C%20happy%20healthy%20pet%20ready%20for%20adoption%2C%20professional%20animal%20photography%2C%20heartwarming%20close-up%20shot%2C%20loving%20expression&width=800&height=600&seq=adoption-main&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Playful%20rescue%20dog%20playing%20in%20yard%2C%20energetic%20pet%20having%20fun%20outdoors%2C%20professional%20animal%20photography%2C%20action%20shot%20of%20happy%20dog&width=800&height=600&seq=adoption-play&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Rescue%20dog%20sleeping%20peacefully%2C%20calm%20relaxed%20pet%20resting%20comfortably%2C%20professional%20animal%20photography%2C%20serene%20indoor%20setting&width=800&height=600&seq=adoption-rest&orientation=landscape',
    'https://readdy.ai/api/search-image?query=Rescue%20dog%20with%20veterinarian%2C%20healthy%20pet%20receiving%20medical%20care%2C%20professional%20animal%20photography%2C%20clean%20veterinary%20clinic%20setting&width=800&height=600&seq=adoption-vet&orientation=landscape'
  ];

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      alert('¡Enlace copiado al portapapeles!');
      setShowShareModal(false);
    }).catch(() => {
      prompt('Copia este enlace:', url);
      setShowShareModal(false);
    });
  };

  const handleContact = (type: string) => {
    if (!adoptionCase || !shelterInfo) return;
    
    switch (type) {
      case 'whatsapp':
        const message = `Hola, estoy interesado en adoptar a ${adoptionCase.name}. ¿Podrían darme más información?`;
        
        // Usar exactamente el enlace configurado en el panel administrador
        let whatsappUrl = '';
        
        if (adoptionCase.whatsapp_phone) {
          // Si el campo contiene un enlace completo de WhatsApp, usarlo directamente
          if (adoptionCase.whatsapp_phone.includes('wa.me') || adoptionCase.whatsapp_phone.includes('whatsapp.com')) {
            // Si ya es un enlace completo, verificar si tiene mensaje
            if (adoptionCase.whatsapp_phone.includes('text=')) {
              whatsappUrl = adoptionCase.whatsapp_phone;
            } else {
              // Agregar el mensaje al enlace existente
              const separator = adoptionCase.whatsapp_phone.includes('?') ? '&' : '?';
              whatsappUrl = `${adoptionCase.whatsapp_phone}${separator}text=${encodeURIComponent(message)}`;
            }
          } else {
            // Si es solo un número, crear el enlace completo
            const cleanNumber = adoptionCase.whatsapp_phone.replace(/[^0-9]/g, '');
            whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
          }
        } else if (shelterInfo.phone) {
          // Fallback al teléfono del refugio
          const cleanNumber = shelterInfo.phone.replace(/[^0-9]/g, '');
          whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
        } else {
          // Fallback por defecto
          whatsappUrl = `https://wa.me/5215512345678?text=${encodeURIComponent(message)}`;
        }
        
        window.open(whatsappUrl, '_blank');
        break;
        
      case 'call':
        const phoneNumber = adoptionCase.contact_phone || shelterInfo.phone || '+5215512345678';
        window.open(`tel:${phoneNumber}`, '_self');
        break;
        
      case 'email':
        const subject = `Interés en adoptar a ${adoptionCase.name}`;
        const body = `Hola,\n\nEstoy interesado en adoptar a ${adoptionCase.name}. Me gustaría conocer más detalles sobre el proceso de adopción.\n\nGracias.`;
        const email = shelterInfo.email || `adopciones@${shelterInfo.shelter_name?.toLowerCase().replace(/\s+/g, '') || 'huellitasunidas'}.org`;
        window.open(`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_self');
        break;
    }
    setShowContactModal(false);
  };

  const handleAdoptionFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adoptionCase || !shelterInfo) return;

    setSubmittingForm(true);
    const formData = new FormData(e.target as HTMLFormElement);
    
    const requestData = {
      adoption_case_id: adoptionCase.id,
      user_id: shelterInfo.id,
      applicant_name: formData.get('name') as string,
      applicant_city: formData.get('city') as string,
      applicant_phone: formData.get('phone') as string,
      applicant_occupation: formData.get('occupation') as string,
      dog_name: adoptionCase.name,
      status: 'pending'
    };

    try {
      const result = await adminOperation('insert', 'adoption_requests', requestData);
      
      if (result?.error) {
        throw result.error;
      }

      alert('¡Solicitud enviada exitosamente! El refugio se pondrá en contacto contigo pronto.');
      setShowAdoptionForm(false);
      
      // Reset form
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('Error enviando solicitud:', error);
      alert('Error al enviar la solicitud. Por favor intenta de nuevo.');
    } finally {
      setSubmittingForm(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-500 animate-spin mb-4"></i>
          <p className="text-gray-600">Cargando información de adopción...</p>
        </div>
      </div>
    );
  }

  if (!adoptionCase) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
          <p className="text-gray-600">No se encontró el caso de adopción</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-10 h-10 object-contain"
              />
              <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                {shelterInfo?.shelter_name || 'Huellitas Unidas'}
              </h1>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleShare}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <i className="ri-share-line"></i>
                <span className="hidden sm:inline">Compartir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">{adoptionCase?.name}</h1>
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                adoptionCase?.adopted 
                  ? 'bg-red-100 text-red-800' 
                  : 'bg-green-100 text-green-800'
              }`}>
                {adoptionCase?.adopted ? 'Ya Adoptado' : 'Disponible para Adopción'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center text-gray-600 space-x-4">
            <span className="flex items-center">
              <i className="ri-map-pin-fill mr-1 text-red-500"></i>
              {adoptionCase?.location}
            </span>
            <span className="flex items-center">
              <i className="ri-calendar-line mr-1"></i>
              {adoptionCase?.age}
            </span>
            <span className="flex items-center">
              <i className="ri-heart-line mr-1"></i>
              {adoptionCase?.breed}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* Image Gallery - Updated for multiple images */}
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={displayImages[currentImageIndex]}
                  alt={`${adoptionCase?.name} - Imagen ${currentImageIndex + 1}`}
                  className="w-full h-96 object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300 rounded-2xl"></div>
                
                {/* Image counter */}
                {displayImages.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {displayImages.length}
                  </div>
                )}
                
                {/* Navigation arrows - Only show if multiple images */}
                {displayImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : displayImages.length - 1)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-gray-800"></i>
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(currentImageIndex < displayImages.length - 1 ? currentImageIndex + 1 : 0)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 p-2 rounded-full shadow-lg transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-gray-800"></i>
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnail Gallery - Only show if multiple images */}
              {displayImages.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {displayImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-20 rounded-lg overflow-hidden cursor-pointer ${
                        currentImageIndex === index ? 'ring-2 ring-blue-500' : ''
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${adoptionCase?.name} - Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      {currentImageIndex !== index && (
                        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Dog Details - Vinculado completamente con BD */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Conoce a {adoptionCase?.name}</h2>
              
              {/* Características - Datos reales de la BD */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-white rounded-xl">
                  <i className="ri-calendar-line text-2xl text-blue-500 mb-2"></i>
                  <p className="text-sm text-gray-600">Edad</p>
                  <p className="font-semibold text-gray-900">{adoptionCase?.age}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl">
                  <i className="ri-heart-pulse-line text-2xl text-red-500 mb-2"></i>
                  <p className="text-sm text-gray-600">Raza</p>
                  <p className="font-semibold text-gray-900">{adoptionCase?.breed}</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl">
                  <i className={`ri-shield-${adoptionCase?.vaccinated ? 'check' : 'cross'}-line text-2xl ${adoptionCase?.vaccinated ? 'text-green-500' : 'text-gray-400'} mb-2`}></i>
                  <p className="text-sm text-gray-600">Vacunas</p>
                  <p className={`font-semibold ${adoptionCase?.vaccinated ? 'text-green-600' : 'text-gray-500'}`}>
                    {adoptionCase?.vaccinated ? 'Al día' : 'Pendiente'}
                  </p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl">
                  <i className={`ri-scissors-${adoptionCase?.sterilized ? 'fill' : 'line'}-line text-2xl ${adoptionCase?.sterilized ? 'text-purple-500' : 'text-gray-400'} mb-2`}></i>
                  <p className="text-sm text-gray-600">Esterilización</p>
                  <p className={`font-semibold ${adoptionCase?.sterilized ? 'text-purple-600' : 'text-gray-500'}`}>
                    {adoptionCase?.sterilized ? 'Sí' : 'No'}
                  </p>
                </div>
              </div>

              {/* Estado de Salud - Basado en datos reales */}
              <div className="bg-white rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Estado de Salud</h3>
                <div className="flex flex-wrap gap-2">
                  {adoptionCase?.vaccinated && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      <i className="ri-shield-check-fill mr-1"></i>Vacunado
                    </span>
                  )}
                  {adoptionCase?.sterilized && (
                    <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                      <i className="ri-scissors-fill mr-1"></i>Esterilizado
                    </span>
                  )}
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    <i className="ri-heart-fill mr-1"></i>Saludable
                  </span>
                </div>
              </div>
            </div>

            {/* Historia de Adopción - Usando descripción real de la BD como historia */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Historia de {adoptionCase?.name}</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  {adoptionCase?.description}
                </p>
              </div>
            </div>

            {/* Requisitos para Adopción */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Requisitos para Adopción</h2>
              
              {/* Mostrar requisitos personalizados si existen */}
              {(adoptionCase?.requirement_1 || adoptionCase?.requirement_2 || adoptionCase?.requirement_3 || adoptionCase?.requirement_4 || adoptionCase?.requirement_5) ? (
                <div className="space-y-3">
                  {adoptionCase?.requirement_1 && (
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p className="text-gray-700">{adoptionCase.requirement_1}</p>
                    </div>
                  )}
                  {adoptionCase?.requirement_2 && (
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p className="text-gray-700">{adoptionCase.requirement_2}</p>
                    </div>
                  )}
                  {adoptionCase?.requirement_3 && (
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p className="text-gray-700">{adoptionCase.requirement_3}</p>
                    </div>
                  )}
                  {adoptionCase?.requirement_4 && (
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p className="text-gray-700">{adoptionCase.requirement_4}</p>
                    </div>
                  )}
                  {adoptionCase?.requirement_5 && (
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-green-500 mt-1"></i>
                      <p className="text-gray-700">{adoptionCase.requirement_5}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No se han definido requisitos específicos para esta adopción.
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Action Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Contact Card */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-heart-fill text-2xl text-blue-500"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">¿Quieres adoptar a {adoptionCase?.name}?</h3>
                  <p className="text-gray-600 text-sm">Contáctanos para iniciar el proceso de adopción</p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => setShowAdoptionForm(true)}
                    disabled={adoptionCase?.adopted}
                    className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer ${
                      adoptionCase?.adopted
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <i className="ri-heart-fill mr-2"></i>
                    {adoptionCase?.adopted ? 'Ya Adoptado' : 'Iniciar Adopción'}
                  </button>

                  <button
                    onClick={() => handleContact('whatsapp')}
                    className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
                  >
                    <i className="ri-whatsapp-fill mr-2"></i>
                    WhatsApp
                  </button>

                  <button
                    onClick={() => handleContact('call')}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
                  >
                    <i className="ri-phone-fill mr-2"></i>
                    Llamar
                  </button>
                </div>
              </div>

              {/* Información del Refugio - Datos reales */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Información del Refugio</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <i className="ri-building-fill text-blue-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{shelterInfo?.shelter_name || 'Huellitas Unidas'}</p>
                      <p className="text-sm text-gray-600">Refugio Certificado</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-map-pin-fill text-green-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {shelterInfo ? `${shelterInfo.city}, ${shelterInfo.state}` : adoptionCase?.location}
                      </p>
                      <p className="text-sm text-gray-600">Ubicación</p>
                    </div>
                  </div>

                  {(adoptionCase?.contact_phone || shelterInfo?.phone) && (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <i className="ri-phone-fill text-orange-500"></i>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{adoptionCase?.contact_phone || shelterInfo?.phone}</p>
                        <p className="text-sm text-gray-600">Teléfono</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <i className="ri-shield-check-fill text-purple-500"></i>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Verificado</p>
                      <p className="text-sm text-gray-600">Refugio confiable</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Share Section */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Ayuda a {adoptionCase?.name}</h3>
                <p className="text-gray-600 text-sm mb-4">
                  Comparte esta adopción con tus amigos y familiares para ayudar a {adoptionCase?.name} a encontrar su hogar perfecto.
                </p>
                <button
                  onClick={handleShare}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-share-line mr-2"></i>
                  Compartir Adopción
                </button>
              </div>

              {/* Información de Fechas - Datos reales de BD */}
              <div className="bg-blue-50 rounded-2xl p-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-1">Publicado el</p>
                  <p className="font-medium text-gray-800">
                    {adoptionCase && new Date(adoptionCase.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  {adoptionCase?.updated_at && adoptionCase.updated_at !== adoptionCase.created_at && (
                    <>
                      <p className="text-xs text-gray-500 mt-2">Actualizado el</p>
                      <p className="text-xs text-gray-600">
                        {new Date(adoptionCase.updated_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Adoption Form Modal */}
      {showAdoptionForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-fill text-2xl text-blue-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Solicitud de Adopción</h3>
              <p className="text-gray-600">Completa tus datos para adoptar a {adoptionCase?.name}</p>
            </div>

            <form onSubmit={handleAdoptionFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre completo *
                </label>
                <input
                  name="name"
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tu nombre completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad *
                </label>
                <input
                  name="city"
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tu ciudad"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  name="phone"
                  type="tel"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tu número de teléfono"
                />
                </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ocupación *
                </label>
                <input
                  name="occupation"
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Tu ocupación"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAdoptionForm(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submittingForm}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer disabled:opacity-50"
                >
                  {submittingForm ? 'Enviando...' : 'Enviar Datos al Refugio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-fill text-2xl text-blue-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Contactar para Adopción</h3>
              <p className="text-gray-600">Elige cómo te gustaría contactarnos</p>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleContact('whatsapp')}
                className="w-full bg-green-500 hover:bg-green-6
0 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
              >
                <i className="ri-whatsapp-fill mr-2"></i>
                WhatsApp
              </button>

              <button
                onClick={() => handleContact('call')}
                className="w-full bg-orange-500 hover:bg-orange-6
0 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
              >
                <i className="ri-phone-fill mr-2"></i>
                Llamar Ahora
              </button>

              <button
                onClick={() => handleContact('email')}
                className="w-full bg-blue-500 hover:bg-blue-6
0 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer flex items-center justify-center"
              >
                <i className="ri-mail-fill mr-2"></i>
                Enviar Email
              </button>
            </div>

            <button
              onClick={() => setShowContactModal(false)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-share-fill text-2xl text-purple-500"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Compartir Adopción</h3>
              <p className="text-gray-600">Ayuda a {adoptionCase?.name} encontrando un hogar</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enlace de la adopción:
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={window.location.href}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                />
                <button
                  onClick={handleCopyLink}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 cursor-pointer"
                >
                  Copiar
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowShareModal(false)}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Contact Footer */}
      <ContactFooter />
    </div>
  );
}